<template>
	<div class="new-event-hotel-booking-form">
		<section class="new-event-hotel-booking-form__summary">
			<button
				type="button"
				class="new-event-hotel-booking-form__toggle"
				:aria-label="translation.reservationData"
			></button>
			<h3>{{translation.reservationData}}</h3>

			<div class="new-event-hotel-booking-form__summary-lines">
				<p><strong>{{translation.hotel}}:</strong> {{bookingRequest.hotel.name}}</p>
				<p><strong>{{translation.room}}:</strong> {{bookingRequest.roomType.name}}</p>
				<p><strong>{{translation.quantity}}:</strong> {{quantityLabel}}</p>
				<p><strong>{{translation.checkin}}:</strong> {{bookingRequest.checkin}}</p>
				<p><strong>{{translation.checkout}}:</strong> {{bookingRequest.checkout}}</p>
				<p><strong>{{translation.dailyPrice}}:</strong> {{formatCurrency(dailyPriceWithoutTaxes, bookingRequest.price.priceCurrency)}}</p>
				<p><strong>{{translation.breakfast}}:</strong> {{mealPlan(bookingRequest.price.mealPlan)}}</p>
				<p><strong>{{translation.taxes}}:</strong> {{formatCurrency(taxes, bookingRequest.price.priceCurrency)}}</p>
				<p><strong>{{translation.total}}:</strong> {{formatCurrency(bookingRequest.price.price, bookingRequest.price.priceCurrency)}}</p>
			</div>
		</section>

		<b-form class="new-event-hotel-booking-form__form" @submit.prevent="submit">
			<button
				type="button"
				class="new-event-hotel-booking-form__toggle"
				:aria-label="translation.fillFields"
			></button>
			<h3>{{translation.fillFields}}</h3>

			<div class="new-event-hotel-booking-form__rooms">
				<section
					v-for="(room, roomIndex) in bookingRequest.rooms"
					:key="roomIndex"
					class="new-event-hotel-booking-form__room"
				>
					<h4 v-if="bookingRequest.rooms.length > 1">
						{{translation.roomGuests}} {{roomIndex + 1}}
					</h4>

					<div
						v-for="(guest, guestIndex) in room.guests"
						:key="`${roomIndex}-${guestIndex}`"
						class="new-event-hotel-booking-form__guest"
					>
						<b-form-input
							v-model="guest.name"
							class="new-event-hotel-booking-form__control"
							type="text"
							:placeholder="translation.name"
							required
						/>

						<label class="new-event-hotel-booking-form__holder">
							<input
								v-model="selectedHolderKey"
								type="radio"
								name="new-event-hotel-holder"
								:value="holderKey(roomIndex, guestIndex)"
								required
							>
							<span>{{translation.pax}}</span>
						</label>
					</div>
				</section>
			</div>

			<p class="new-event-hotel-booking-form__pax-label">
				{{translation.paxInfo}}
			</p>

			<div class="new-event-hotel-booking-form__pax-grid">
				<b-form-input
					v-model="formPax.email"
					class="new-event-hotel-booking-form__control"
					type="email"
					:placeholder="translation.email"
					required
				/>
				<b-form-input
					v-model="formPax.phone"
					class="new-event-hotel-booking-form__control"
					type="tel"
					:placeholder="translation.phone"
					required
				/>
				<div class="new-event-hotel-booking-form__select-field">
					<b-form-select
						v-model="selectedCountryId"
						class="new-event-hotel-booking-form__control new-event-hotel-booking-form__select"
						:options="countryOptions"
						required
					/>
				</div>
				<b-form-input
					v-model="bookingRequest.obs"
					class="new-event-hotel-booking-form__control"
					type="text"
					:placeholder="translation.specialRequest"
				/>
			</div>

			<div class="new-event-hotel-booking-form__actions">
				<button type="button" class="new-event-hotel-booking-form__button new-event-hotel-booking-form__button--secondary" @click="$emit('back')">
					voltar
				</button>
				<button type="submit" class="new-event-hotel-booking-form__button new-event-hotel-booking-form__button--primary">
					{{lowercaseLabel(translation.continue)}}
				</button>
			</div>
		</b-form>
	</div>
</template>

<script>
import { translate } from '../../assets/translations';
import AllocationTypes from '../../enums/AllocationTypes';

export default {
	props: {
		bookingRequest: {
			type: Object,
			required: true,
		},
		countries: {
			type: Array,
			default: () => [],
		},
		pax: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			formPax: Object.assign({}, this.pax),
			selectedCountryId: this.pax && this.pax.country ? this.pax.country.id : null,
			selectedHolderKey: null,
		};
	},
	computed: {
		allocationTypeText() {
			const allocationType = this.bookingRequest.price
				&& this.bookingRequest.price.roomAllocation
				? this.bookingRequest.price.roomAllocation.allocationType
				: null;
			return AllocationTypes[allocationType]
				? this.translation[AllocationTypes[allocationType].nameProperty]
				: '';
		},
		countryOptions() {
			const placeholder = {
				value: null,
				text: this.lowercaseLabel(this.translation.country),
				disabled: true,
			};
			return [placeholder].concat(this.countries.map(country => ({
				value: country.id,
				text: country.name,
			})));
		},
		dailyPriceWithoutTaxes() {
			return this.normalizeCurrencyValue(this.bookingRequest.price.price) - this.taxes;
		},
		quantityLabel() {
			const roomsQty = this.bookingRequest.rooms ? this.bookingRequest.rooms.length : 0;
			const allocationType = this.allocationTypeText ? ` ${this.allocationTypeText}` : '';
			return `${roomsQty} ${this.bookingRequest.roomType.name}${allocationType}`;
		},
		taxes() {
			return this.normalizeCurrencyValue(this.bookingRequest.price.price) * 0.10;
		},
		translation() {
			return translate(this.$store.state.language).hotelBookingPersonalInfo;
		},
	},
	methods: {
		countryForId(countryId) {
			return this.countries.find(country => country.id === countryId) || { id: countryId };
		},
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		holderGuest() {
			if (!this.selectedHolderKey) return null;
			const parts = this.selectedHolderKey.split('-');
			const room = this.bookingRequest.rooms[Number(parts[0])];
			return room && room.guests ? room.guests[Number(parts[1])] : null;
		},
		holderKey(roomIndex, guestIndex) {
			return `${roomIndex}-${guestIndex}`;
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		mealPlan(meal) {
			return meal ? this.translation.breakfastIncluded : this.translation.breakfastNotIncluded;
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
		submit() {
			const holder = this.holderGuest();
			if (!holder || !holder.name) {
				alert('Selecione um hospede responsavel pela reserva.');
				return;
			}

			const pax = Object.assign({}, this.formPax, {
				name: holder.name,
				country: this.countryForId(this.selectedCountryId),
			});

			this.$emit('continue', {
				bookingRequest: this.bookingRequest,
				pax,
			});
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-hotel-booking-form {
	box-sizing: border-box;
	padding: 44px 26px 52px;
	background: #eeeeee;
	color: #5a5a5a;
}

.new-event-hotel-booking-form__toggle {
	display: block;
	width: 23px;
	height: 12px;
	margin: 0 0 27px;
	padding: 0;
	border: 0;
	background: url("data:image/svg+xml,%3Csvg width='23' height='12' viewBox='0 0 23 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2 2L11.5 10L21 2' stroke='%2366ACC6' stroke-width='3'/%3E%3C/svg%3E") center / contain no-repeat;
	cursor: default;
}

.new-event-hotel-booking-form h3 {
	margin: 0 0 45px;
	color: #002c41;
	font-size: 18px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-hotel-booking-form__summary-lines {
	margin-bottom: 57px;
	font-size: 18px;
	font-weight: 700;
	line-height: 40px;
}

.new-event-hotel-booking-form__summary-lines p {
	margin: 0;
}

.new-event-hotel-booking-form__form {
	margin-top: 0;
}

.new-event-hotel-booking-form__rooms,
.new-event-hotel-booking-form__pax-grid {
	display: flex;
	flex-direction: column;
	gap: 16px;
	max-width: 739px;
}

.new-event-hotel-booking-form__room h4 {
	margin: 0 0 14px;
	color: #002c41;
	font-size: 17px;
	font-weight: 700;
	line-height: 21px;
}

.new-event-hotel-booking-form__guest {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 24px;
	margin-bottom: 16px;
}

.new-event-hotel-booking-form__guest .form-control {
	flex: 1 1 520px;
}

.new-event-hotel-booking-form__holder {
	display: inline-flex;
	align-items: center;
	gap: 13px;
	margin: 0;
	color: #5a5a5a;
	font-size: 15px;
	font-weight: 400;
	line-height: 18px;
	white-space: nowrap;
	cursor: pointer;
}

.new-event-hotel-booking-form__holder input {
	width: 14px;
	height: 14px;
	margin: 0;
	accent-color: #5a5a5a;
}

.new-event-hotel-booking-form__pax-label {
	margin: 38px 0 24px;
	color: #5a5a5a;
	font-size: 15px;
	font-weight: 400;
	line-height: 18px;
}

.new-event-hotel-booking-form__select-field {
	position: relative;
}

.new-event-hotel-booking-form__select-field::after {
	content: "";
	position: absolute;
	top: 22px;
	right: 19px;
	width: 13px;
	height: 13px;
	border-right: 3px solid #66acc6;
	border-bottom: 3px solid #66acc6;
	pointer-events: none;
	transform: rotate(45deg) translateY(-4px);
}

.new-event-hotel-booking-form__control,
.new-event-hotel-booking-form /deep/ .form-control,
.new-event-hotel-booking-form__select {
	display: block;
	box-sizing: border-box;
	width: 100%;
	height: 58px;
	border: 1px solid rgba(0, 0, 0, .2);
	border-radius: 10px;
	background: #ffffff;
	color: #5a5a5a;
	font-family: inherit;
	font-size: 18px;
	font-weight: 400;
	line-height: 18px;
	outline: none;
	box-shadow: none;
}

.new-event-hotel-booking-form__select {
	padding-right: 54px;
	background-image: none;
	appearance: none;
}

.new-event-hotel-booking-form__control::placeholder,
.new-event-hotel-booking-form /deep/ .form-control::placeholder {
	color: #5a5a5a;
	opacity: 1;
}

.new-event-hotel-booking-form__control:focus,
.new-event-hotel-booking-form /deep/ .form-control:focus,
.new-event-hotel-booking-form__select:focus {
	border-color: #66acc6;
	box-shadow: 0 0 0 .2rem rgba(102, 172, 198, .15);
}

.new-event-hotel-booking-form__actions {
	display: flex;
	gap: 13px;
	margin-top: 32px;
}

.new-event-hotel-booking-form__button {
	box-sizing: border-box;
	width: 153px;
	height: 36px;
	border-radius: 10px;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-hotel-booking-form__button--secondary {
	border: 1px solid #ff9600;
	background: #ffffff;
	color: #ff9600;
}

.new-event-hotel-booking-form__button--primary {
	border: 1px solid #ff9600;
	background: #ff9600;
	color: #ffffff;
}

@media (max-width: 900px) {
	.new-event-hotel-booking-form {
		padding: 32px 22px 38px;
	}

	.new-event-hotel-booking-form h3 {
		margin-bottom: 28px;
	}

	.new-event-hotel-booking-form__summary-lines {
		margin-bottom: 40px;
		font-size: 15px;
		line-height: 28px;
	}

	.new-event-hotel-booking-form__guest {
		flex-direction: column;
		align-items: stretch;
		gap: 12px;
	}

	.new-event-hotel-booking-form__guest .form-control {
		flex: none;
	}

	.new-event-hotel-booking-form__actions {
		flex-wrap: wrap;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/HotelBookingPersonalInfo.vue